import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { ShellService } from '../shell/shell.service';
import { BusyBlock, CalendarAccess, CalendarEvent, CalendarService } from './calendar.service';

type CalendarView = 'month' | 'week' | 'day';
// Mockup calendar-filters.js: one colour category per event, and the status filter by category.
type EventCategory = 'inspection' | 'reinspection' | 'work' | 'completed' | 'on-hold' | 'cancelled';
type StatusFilter = 'all' | 'inspection' | 'reinspection' | 'work-completed' | 'on-hold' | 'cancelled';
const CATEGORY_LABELS = { inspection: 'Inspection', reinspection: 'Reinspection', work: 'Work', completed: 'Completed',
    'on-hold': 'On hold', cancelled: 'Cancelled' };

// One line in a day: an Inspectmite appointment, or a Busy block from the inspector's own calendar.
interface DayEntry {
    event?: CalendarEvent;
    busy?: BusyBlock;
}

interface DayCell {
    date: string;           // YYYY-MM-DD
    day: number;
    inMonth: boolean;
    isToday: boolean;
    events: CalendarEvent[];
    entries: DayEntry[];    // events and Busy blocks, in time order
}

// Job dates are stored and returned in the app timezone (Laravel config app.timezone), so
// "today" is taken in that timezone too, not the browser's.
const APP_TIMEZONE = 'America/Los_Angeles';
const MONTH_VISIBLE = 3;                      // events shown per month cell before "+N more"
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/**
 * Calendar (Inspectmite redesign), replacing twin-ci's administrator/staff/inspector calendar
 * pages. Month, week and day views over api/calendar/events; which jobs come back depends on
 * the user's role and is enforced by the API (see CalendarService / CalendarController).
 * Clicking an event opens the job's overview.
 *
 * Built without a calendar library: dates are handled as YYYY-MM-DD strings with UTC arithmetic,
 * so daylight-saving changes never shift a day.
 */
@Component({
    selector: 'app-calendar',
    templateUrl: './calendar.component.html',
    styleUrls: ['./calendar.component.scss']
})
export class CalendarComponent implements OnInit, OnDestroy {
    readonly weekdays = WEEKDAYS;
    readonly visible = MONTH_VISIBLE;

    access: CalendarAccess = null;
    state: 'loading' | 'ready' | 'error' | 'denied' = 'loading';

    view: CalendarView = 'month';
    cursor = todayInAppTimezone();    // the date the view is anchored on
    today = todayInAppTimezone();
    title = '';

    search = '';
    status: StatusFilter = 'all';
    // Inspector calendars shown ('all' scope): null = every inspector (and unassigned jobs).
    // Saved per user as the 'calendar_inspectors' preference.
    private selectedInspectors: number[] = null;
    createRoute = '';

    weeks: DayCell[][] = [];          // month view
    days: DayCell[] = [];             // week and day views
    total = 0;

    private events: CalendarEvent[] = [];
    private busy: BusyBlock[] = [];
    unavailable: number[] = [];       // inspectors whose own calendar could not be read
    private rangeStart = '';
    private rangeEnd = '';
    private request: Subscription;
    private busyRequest: Subscription;
    private prefsSub: Subscription;
    private settingsSub: Subscription;

    constructor(private api: CalendarService, private shell: ShellService) { }

    ngOnInit() {
        this.prefsSub = this.shell.preferences$.subscribe(() => {
            const inspectors = this.shell.get('calendar_inspectors', null);
            this.selectedInspectors = Array.isArray(inspectors) ? inspectors.map(Number) : null;
            const saved = this.shell.get('calendar_view', 'month');
            if (saved !== this.view && ['month', 'week', 'day'].indexOf(saved) !== -1) {
                this.view = saved;
                if (this.access) {
                    this.load();
                }
            } else if (this.access) {
                this.build();
            }
        });
        // Same rule as the sidebar's Create New Inspection link.
        this.settingsSub = this.shell.settings$.subscribe(settings => {
            this.createRoute = settings.inspection_tab_order == 1 ? '/inspections-edit-property'
                : settings.inspection_tab_order == 0 ? '/inspections-order-parties' : '';
        });
        this.api.access().subscribe(access => {
            this.access = access;
            this.load();
        }, err => this.state = err && err.status === 403 ? 'denied' : 'error');
    }

    ngOnDestroy() {
        if (this.request) {
            this.request.unsubscribe();
        }
        if (this.busyRequest) {
            this.busyRequest.unsubscribe();
        }
        this.prefsSub.unsubscribe();
        this.settingsSub.unsubscribe();
    }

    // ---- Navigation ----

    setView(view: CalendarView) {
        if (view !== this.view) {
            this.view = view;
            this.shell.set('calendar_view', view);   // remembered per user, across devices
            this.load();
        }
    }

    goToday() {
        this.today = todayInAppTimezone();
        this.cursor = this.today;
        this.load();
    }

    step(direction: number) {
        if (this.view === 'month') {
            this.cursor = addMonths(this.cursor, direction);
        } else {
            this.cursor = addDays(this.cursor, direction * (this.view === 'week' ? 7 : 1));
        }
        this.load();
    }

    openDay(date: string) {
        this.cursor = date;
        if (this.view === 'day') {
            this.load();
        } else {
            this.setView('day');      // loads
        }
    }

    // ---- Filters ----

    onFilterChange() {
        this.build();                 // search, status and inspectors are filtered here
    }

    get allInspectors(): boolean {
        return this.selectedInspectors === null;
    }

    get shownInspectors(): number[] {
        return this.selectedInspectors === null ? (this.access ? this.access.inspectors.map(i => i.id) : []) : this.selectedInspectors;
    }

    isShown(id: number): boolean {
        return this.selectedInspectors === null || this.selectedInspectors.indexOf(id) !== -1;
    }

    get inspectorLabel(): string {
        const shown = this.shownInspectors;
        if (this.allInspectors) {
            return 'All inspectors';
        }
        if (shown.length === 1) {
            const one = this.access.inspectors.find(i => i.id === shown[0]);
            return one ? one.name : '1 inspector';
        }
        return shown.length ? shown.length + ' inspectors' : 'No inspectors';
    }

    selectAllInspectors(on: boolean) {
        this.saveInspectors(on ? null : []);
    }

    toggleInspector(id: number, on: boolean) {
        const shown = this.shownInspectors.filter(i => i !== id);
        if (on) {
            shown.push(id);
        }
        const all = this.access.inspectors.every(i => shown.indexOf(i.id) !== -1);
        this.saveInspectors(all ? null : shown);
    }

    private saveInspectors(ids: number[]) {
        this.selectedInspectors = ids;
        this.shell.set('calendar_inspectors', ids);   // null removes it: every inspector
        this.build();
        this.loadBusy();
    }

    // ---- Data ----

    private load() {
        const range = this.range();
        this.rangeStart = range[0];
        this.rangeEnd = range[1];
        this.title = this.makeTitle();
        this.state = 'loading';
        if (this.request) {
            this.request.unsubscribe();
        }
        this.request = this.api.events(this.rangeStart, this.rangeEnd).subscribe(res => {
            this.events = res.events;
            this.state = 'ready';
            this.build();
        }, err => this.state = err && err.status === 403 ? 'denied' : 'error');
        this.loadBusy();
    }

    // Busy blocks load on their own: a slow or failing calendar never holds up the appointments.
    private loadBusy() {
        if (this.busyRequest) {
            this.busyRequest.unsubscribe();
        }
        const ids = this.access && this.access.scope === 'all' && !this.allInspectors ? this.shownInspectors : undefined;
        this.busy = [];
        this.unavailable = [];
        if (ids && !ids.length) {
            this.build();
            return;
        }
        this.busyRequest = this.api.busy(this.rangeStart, this.rangeEnd, ids).subscribe(res => {
            this.busy = res.busy;
            this.unavailable = res.unavailable;
            this.build();
        }, () => this.build());
    }

    // The dates the current view shows (month: whole weeks around the month).
    private range(): [string, string] {
        if (this.view === 'day') {
            return [this.cursor, this.cursor];
        }
        if (this.view === 'week') {
            const start = startOfWeek(this.cursor);
            return [start, addDays(start, 6)];
        }
        const first = this.cursor.slice(0, 8) + '01';
        const last = addDays(addMonths(first, 1), -1);
        return [startOfWeek(first), addDays(startOfWeek(last), 6)];
    }

    private build() {
        const byDate: { [date: string]: CalendarEvent[] } = {};
        const filtered = this.events.filter(e => this.matches(e));
        filtered.forEach(e => (byDate[e.date] = byDate[e.date] || []).push(e));
        const busyByDate: { [date: string]: BusyBlock[] } = {};
        this.busy.filter(b => this.busyShown(b)).forEach(b => (busyByDate[b.date] = busyByDate[b.date] || []).push(b));
        // Month view counts only the month itself, not the neighbouring days that fill the grid.
        this.total = filtered.filter(e => this.view === 'month'
            ? e.date.slice(0, 7) === this.cursor.slice(0, 7)
            : e.date >= this.rangeStart && e.date <= this.rangeEnd).length;

        const cell = (date: string): DayCell => {
            const events = (byDate[date] || []).sort((a, b) => (a.anytime === b.anytime ? a.start.localeCompare(b.start) : a.anytime ? -1 : 1));
            // Anytime appointments and all-day Busy blocks first, then by start time.
            const key = (x: DayEntry) => x.event ? (x.event.anytime ? '0' : '1' + x.event.start) : (x.busy.all_day ? '0' : '1' + x.busy.start);
            const entries = events.map(e => ({ event: e }) as DayEntry)
                .concat((busyByDate[date] || []).map(b => ({ busy: b }) as DayEntry))
                .sort((a, b) => key(a).localeCompare(key(b)));
            return {
                date,
                day: Number(date.slice(8, 10)),
                inMonth: date.slice(0, 7) === this.cursor.slice(0, 7),
                isToday: date === this.today,
                events,
                entries
            };
        };

        this.weeks = [];
        this.days = [];
        if (this.view === 'month') {
            for (let d = this.rangeStart; d <= this.rangeEnd; d = addDays(d, 7)) {
                this.weeks.push([0, 1, 2, 3, 4, 5, 6].map(i => cell(addDays(d, i))));
            }
        } else {
            for (let d = this.rangeStart; d <= this.rangeEnd; d = addDays(d, 1)) {
                this.days.push(cell(d));
            }
        }
    }

    private matches(e: CalendarEvent): boolean {
        if (this.access && this.access.scope === 'all' && !this.isShown(e.agent_id)) {
            return false;
        }
        const category = this.category(e);
        if (this.status === 'work-completed' ? (category !== 'work' && category !== 'completed')
            : this.status !== 'all' && category !== this.status) {
            return false;
        }
        const q = this.search.trim().toLowerCase();
        if (!q) {
            return true;
        }
        return [e.address, e.city, e.client, e.inspector, e.report_number, e.type]
            .some(v => (v || '').toLowerCase().indexOf(q) !== -1);
    }

    // Busy blocks follow the inspector filter; they have no status or text, so they step aside
    // while a status filter or a search is on.
    private busyShown(b: BusyBlock): boolean {
        if (this.access && this.access.scope === 'all' && !this.isShown(b.agent_id)) {
            return false;
        }
        return this.status === 'all' && !this.search.trim();
    }

    private makeTitle(): string {
        const [y, m, d] = this.cursor.split('-').map(Number);
        if (this.view === 'month') {
            return MONTHS[m - 1] + ' ' + y;
        }
        if (this.view === 'day') {
            return WEEKDAYS[weekday(this.cursor)] + ', ' + MONTHS[m - 1] + ' ' + d + ', ' + y;
        }
        const [sy, sm, sd] = this.rangeStart.split('-').map(Number);
        const [ey, em, ed] = this.rangeEnd.split('-').map(Number);
        const short = (month: number) => MONTHS[month - 1].slice(0, 3);
        return short(sm) + ' ' + sd + (sy !== ey ? ', ' + sy : '') + ' – ' + (em !== sm ? short(em) + ' ' : '') + ed + ', ' + ey;
    }

    // ---- Template helpers ----

    dayHeading(date: string): string {
        const [, m, d] = date.split('-').map(Number);
        return WEEKDAYS[weekday(date)].slice(0, 3) + ' ' + MONTHS[m - 1].slice(0, 3) + ' ' + d;
    }

    category(e: CalendarEvent): EventCategory {
        return e.status === 'Cancelled' ? 'cancelled'
            : e.status === 'Hold' ? 'on-hold'
            : e.status === 'Completed' ? 'completed'
            : e.kind === 'work' ? 'work'
            : e.reinspection ? 'reinspection' : 'inspection';
    }

    // Mockup .calendar-event-meta: "Inspector · Category".
    eventMeta(e: CalendarEvent): string {
        const label = CATEGORY_LABELS[this.category(e)];
        return this.access && this.access.scope === 'all' && e.inspector ? e.inspector + ' · ' + label : label;
    }

    trackByDate(index: number, cell: DayCell) {
        return cell.date;
    }

    trackByEntry(index: number, x: DayEntry) {
        return x.event ? 'job' + x.event.job_id : 'busy' + x.busy.agent_id + x.busy.date + x.busy.start;
    }

    // "Busy · 9:00 AM – 10:30 AM" (or "All day"), with the inspector's name for 'all' scope.
    busyLabel(b: BusyBlock): string {
        return b.all_day ? 'All day' : clock(b.start) + ' – ' + clock(b.end);
    }

    busyMeta(b: BusyBlock): string {
        return this.access && this.access.scope === 'all' ? this.inspectorName(b.agent_id) : 'Your calendar';
    }

    get unavailableNote(): string {
        if (!this.unavailable.length) {
            return '';
        }
        if (!this.access || this.access.scope === 'own') {
            return "Couldn't read your connected calendar, so some busy times may be missing. Reconnect it if this keeps happening.";
        }
        const names = this.unavailable.map(id => this.inspectorName(id));
        return "Couldn't read the connected calendar of " + names.join(', ') + ', so some busy times may be missing.';
    }

    private inspectorName(id: number): string {
        const one = this.access && this.access.inspectors.find(i => i.id === id);
        return one ? one.name : 'Inspector';
    }
}

// ---- Date helpers (YYYY-MM-DD strings, UTC arithmetic) ----

// "HH:mm" (24:00 = midnight at the end of the day) as "9:00 AM".
function clock(time: string): string {
    const [h, m] = time.split(':').map(Number);
    return ((h % 12) || 12) + ':' + (m < 10 ? '0' : '') + m + ' ' + (h % 24 < 12 ? 'AM' : 'PM');
}

function todayInAppTimezone(): string {
    try {
        // en-CA formats as YYYY-MM-DD.
        return new Intl.DateTimeFormat('en-CA', { timeZone: APP_TIMEZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
    } catch (e) {
        const now = new Date();
        return iso(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
    }
}

function toUtc(date: string): number {
    const [y, m, d] = date.split('-').map(Number);
    return Date.UTC(y, m - 1, d);
}

function iso(time: number): string {
    return new Date(time).toISOString().slice(0, 10);
}

function addDays(date: string, days: number): string {
    return iso(toUtc(date) + days * 86400000);
}

function addMonths(date: string, months: number): string {
    const [y, m] = date.split('-').map(Number);
    return iso(Date.UTC(y, m - 1 + months, 1));
}

function weekday(date: string): number {
    return new Date(toUtc(date)).getUTCDay();
}

function startOfWeek(date: string): string {
    return addDays(date, -weekday(date));   // weeks start on Sunday, as in the design
}
